import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  X,
  Star,
  Flame,
  BookOpen,
  Shield,
  RotateCcw,
  ChevronRight,
  Lock,
  LogOut,
  CreditCard,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { useNavigate } from 'react-router-dom';
import { BADGES, getParentBadges, getKidBadges } from '../data/badges';
import type { AgeGroup } from '../types';

const AVATARS = ['🦁', '🐼', '🦊', '🐸', '🦋', '🦄'];
const AGE_GROUPS: { label: string; value: AgeGroup }[] = [
  { label: '4–6', value: '4-6' },
  { label: '7–9', value: '7-9' },
  { label: '10–12', value: '10-12' },
];

interface AddChildForm {
  name: string;
  ageGroup: AgeGroup;
  avatar: string;
}

const DEFAULT_CHILD_FORM: AddChildForm = {
  name: '',
  ageGroup: '7-9',
  avatar: '🦁',
};

export default function Profile() {
  const navigate = useNavigate();
  const user = useAppStore((s) => s.user);
  const appMode = useAppStore((s) => s.appMode);
  const setAppMode = useAppStore((s) => s.setAppMode);
  const setUser = useAppStore((s) => s.setUser);
  const setActiveChild = useAppStore((s) => s.setActiveChild);
  const logout = useAppStore((s) => s.logout);
  const activeChildId = useAppStore((s) => s.activeChildId);
  const getActiveChild = useAppStore((s) => s.getActiveChild);

  const [showAddChild, setShowAddChild] = useState(false);
  const [childForm, setChildForm] = useState<AddChildForm>(DEFAULT_CHILD_FORM);
  const [cancelLoading, setCancelLoading] = useState(false);

  const activeChild = getActiveChild();
  const parentBadges = getParentBadges();
  const kidBadges = getKidBadges();

  const handleAddChild = () => {
    if (!user || !childForm.name.trim()) return;

    const childId = 'child-' + Date.now();
    const newChild = {
      id: childId,
      userId: user.id,
      name: childForm.name.trim(),
      ageGroup: childForm.ageGroup,
      avatar: childForm.avatar,
      points: 0,
      streak: 0,
      practicedScenarioIds: [],
      savedScenarioIds: [],
      earnedBadgeIds: [],
    };

    setUser({
      ...user,
      childProfiles: [...user.childProfiles, newChild],
    });
    setActiveChild(childId);
    setChildForm(DEFAULT_CHILD_FORM);
    setShowAddChild(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/onboarding');
  };

  const handleManageSubscription = async () => {
    if (!user?.stripeCustomerId) {
      alert('No billing account found. Please contact support.');
      return;
    }
    setCancelLoading(true);
    try {
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripeCustomerId: user.stripeCustomerId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed');
      window.location.href = data.url;
    } catch {
      alert('Could not open billing portal. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResetOnboarding = () => {
    if (confirm('This will reset your user data and restart onboarding. Are you sure?')) {
      setUser(null);
      setActiveChild(null);
      window.location.href = '/onboarding';
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <div className="text-5xl mb-4">👤</div>
        <p className="text-gray-500">No profile found.</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-24 max-w-2xl mx-auto space-y-6">
      {/* Page header */}
      <div className="pt-4">
        <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-800">
          Profile
        </h1>
      </div>

      {/* Parent profile card */}
      <section className="bg-gradient-to-br from-primary-600 to-violet-700 rounded-3xl p-5 text-white shadow-lg">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <Shield size={28} className="text-white" />
          </div>
          <div>
            <p className="text-white/70 text-xs font-semibold uppercase tracking-wide">
              Parent / Carer
            </p>
            <h2 className="font-display text-xl font-extrabold text-white">
              {user.name}
            </h2>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1 bg-white/15 rounded-2xl p-3 text-center">
            <p className="font-display font-extrabold text-2xl">{user.points}</p>
            <p className="text-white/70 text-xs font-semibold">Points</p>
          </div>
          <div className="flex-1 bg-white/15 rounded-2xl p-3 text-center">
            <p className="font-display font-extrabold text-2xl">
              {user.earnedBadgeIds.length}
            </p>
            <p className="text-white/70 text-xs font-semibold">Badges</p>
          </div>
          <div className="flex-1 bg-white/15 rounded-2xl p-3 text-center">
            <p className="font-display font-extrabold text-2xl">
              {user.contributionCount}
            </p>
            <p className="text-white/70 text-xs font-semibold">Submitted</p>
          </div>
        </div>
      </section>

      {/* Parent badges */}
      <section>
        <h2 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
          <Star size={14} className="text-warm-500" />
          Parent Badges
        </h2>
        <div className="grid grid-cols-3 gap-3">
          {parentBadges.map((badge) => {
            const earned = user.earnedBadgeIds.includes(badge.id);
            return (
              <div
                key={badge.id}
                className={`rounded-2xl p-3 text-center transition-all ${
                  earned ? badge.color : 'bg-gray-100 opacity-50'
                }`}
              >
                <div className="text-3xl mb-1">
                  {earned ? badge.icon : <Lock size={22} className="mx-auto text-gray-400" />}
                </div>
                <p className="text-xs font-bold leading-tight line-clamp-2">
                  {badge.name}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Child profiles */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
            <BookOpen size={14} />
            Children
          </h2>
          <button
            onClick={() => setShowAddChild(true)}
            className="flex items-center gap-1 text-primary-600 font-bold text-sm"
          >
            <Plus size={15} />
            Add Child
          </button>
        </div>

        {user.childProfiles.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-2xl">
            <div className="text-4xl mb-2">👧</div>
            <p className="text-sm font-semibold text-gray-500">No children added yet</p>
            <button
              onClick={() => setShowAddChild(true)}
              className="mt-3 text-primary-600 text-sm font-bold"
            >
              Add your first child
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {user.childProfiles.map((child) => {
              const isActive = child.id === activeChildId;
              return (
                <motion.div
                  key={child.id}
                  layout
                  className={`bg-white rounded-2xl p-4 shadow-card border-2 transition-all ${
                    isActive
                      ? 'border-primary-400'
                      : 'border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-4xl">{child.avatar}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-gray-800">
                          {child.name}
                        </h3>
                        {isActive && (
                          <span className="bg-primary-100 text-primary-700 text-xs font-bold px-2 py-0.5 rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400 text-xs">
                        Ages {child.ageGroup}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-primary-50 rounded-xl p-2 text-center">
                      <p className="font-display font-bold text-primary-700 text-lg">
                        {child.points}
                      </p>
                      <p className="text-primary-500 text-[10px] font-semibold">
                        Points
                      </p>
                    </div>
                    <div className="bg-warm-50 rounded-xl p-2 text-center">
                      <p className="font-display font-bold text-warm-600 text-lg flex items-center justify-center gap-0.5">
                        {child.streak}
                        <Flame size={13} />
                      </p>
                      <p className="text-warm-600 text-[10px] font-semibold">
                        Streak
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-2 text-center">
                      <p className="font-display font-bold text-green-700 text-lg">
                        {child.practicedScenarioIds.length}
                      </p>
                      <p className="text-green-600 text-[10px] font-semibold">
                        Practised
                      </p>
                    </div>
                  </div>

                  {/* Kid badges for this child */}
                  {child.earnedBadgeIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {child.earnedBadgeIds.map((id) => {
                        const b = BADGES.find((badge) => badge.id === id);
                        return b ? (
                          <span
                            key={id}
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${b.color}`}
                          >
                            {b.icon} {b.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {!isActive && (
                      <button
                        onClick={() => setActiveChild(child.id)}
                        className="flex-1 text-xs font-bold text-primary-600 border border-primary-200 py-2 rounded-xl hover:bg-primary-50 transition-colors"
                      >
                        Set Active
                      </button>
                    )}
                    <button
                      onClick={() => setAppMode('kid')}
                      className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-warm-400 py-2 rounded-xl hover:bg-warm-500 transition-colors"
                    >
                      <Star size={12} />
                      Switch to Kid View
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Kid badges display */}
      {activeChild && (
        <section>
          <h2 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
            <Star size={14} className="text-primary-500" />
            {activeChild.name}'s Badges
          </h2>
          <div className="grid grid-cols-3 gap-3">
            {kidBadges.map((badge) => {
              const earned = activeChild.earnedBadgeIds.includes(badge.id);
              return (
                <div
                  key={badge.id}
                  className={`rounded-2xl p-3 text-center transition-all ${
                    earned ? badge.color : 'bg-gray-100 opacity-50'
                  }`}
                  title={badge.description}
                >
                  <div className="text-3xl mb-1">
                    {earned ? (
                      badge.icon
                    ) : (
                      <Lock size={22} className="mx-auto text-gray-400" />
                    )}
                  </div>
                  <p className="text-xs font-bold leading-tight line-clamp-2">
                    {badge.name}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Subscription details */}
      {user.tier === 'premium' && (
        <section>
          <h2 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
            <CreditCard size={14} className="text-primary-500" />
            Subscription
          </h2>
          <div className="bg-white rounded-2xl shadow-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Plan</p>
                <p className="font-bold text-gray-800 text-sm">
                  {user.subscriptionInterval === 'annual' ? 'Annual ($23.88/yr)' : 'Monthly ($2.99/mo)'}
                </p>
              </div>
              <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">Active</span>
            </div>
            {user.premiumSince && (
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Member since</p>
                <p className="font-semibold text-gray-700 text-sm">
                  {new Date(user.premiumSince).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            )}
            <button
              onClick={handleManageSubscription}
              disabled={cancelLoading}
              className="w-full text-sm font-bold text-primary-600 border border-primary-200 py-2.5 rounded-xl hover:bg-primary-50 transition-colors disabled:opacity-60"
            >
              {cancelLoading ? 'Opening portal…' : 'Manage / Cancel Subscription'}
            </button>
          </div>
        </section>
      )}

      {/* Settings */}
      <section>
        <h2 className="font-display font-bold text-gray-700 text-sm uppercase tracking-wide mb-3">
          Settings
        </h2>
        <div className="bg-white rounded-2xl shadow-card overflow-hidden divide-y divide-gray-50">
          {/* Mode toggle */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-primary-50 flex items-center justify-center">
                <Shield size={16} className="text-primary-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-700 text-sm">App Mode</p>
                <p className="text-gray-400 text-xs">
                  Currently: {appMode.mode === 'parent' ? 'Parent' : 'Kid'} Mode
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                setAppMode(appMode.mode === 'parent' ? 'kid' : 'parent')
              }
              className="text-xs font-bold text-primary-600 bg-primary-50 px-3 py-1.5 rounded-xl"
            >
              Toggle
            </button>
          </div>

          {/* Log out */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between p-4 hover:bg-orange-50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-orange-50 flex items-center justify-center group-hover:bg-orange-100">
                <LogOut size={16} className="text-orange-500" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-orange-600 text-sm">Log Out</p>
                <p className="text-gray-400 text-xs">Sign out of your account</p>
              </div>
            </div>
            <ChevronRight size={15} className="text-gray-300" />
          </button>

          {/* Reset onboarding */}
          <button
            onClick={handleResetOnboarding}
            className="w-full flex items-center justify-between p-4 hover:bg-red-50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center group-hover:bg-red-100">
                <RotateCcw size={16} className="text-red-500" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-red-600 text-sm">
                  Reset Onboarding
                </p>
                <p className="text-gray-400 text-xs">
                  Clear data and restart setup
                </p>
              </div>
            </div>
            <ChevronRight size={15} className="text-gray-300" />
          </button>
        </div>
      </section>

      {/* Add child modal */}
      <AnimatePresence>
        {showAddChild && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddChild(false)}
              className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl px-4 pt-5 pb-8"
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-xl font-extrabold text-gray-800">
                  Add a Child
                </h2>
                <button
                  onClick={() => setShowAddChild(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Child's Name
                  </label>
                  <input
                    type="text"
                    value={childForm.name}
                    onChange={(e) =>
                      setChildForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="e.g. Lily"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                    autoFocus
                  />
                </div>

                {/* Age */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Age Group
                  </label>
                  <div className="flex gap-2">
                    {AGE_GROUPS.map((ag) => (
                      <button
                        key={ag.value}
                        type="button"
                        onClick={() =>
                          setChildForm((f) => ({ ...f, ageGroup: ag.value }))
                        }
                        className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                          childForm.ageGroup === ag.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-100 text-gray-500'
                        }`}
                      >
                        {ag.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Avatar */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                    Avatar
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {AVATARS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() =>
                          setChildForm((f) => ({ ...f, avatar: emoji }))
                        }
                        className={`text-3xl p-2 rounded-xl transition-all ${
                          childForm.avatar === emoji
                            ? 'bg-primary-100 scale-110'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleAddChild}
                  disabled={!childForm.name.trim()}
                  className="w-full bg-primary-600 text-white py-4 rounded-2xl font-bold text-base shadow-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  Add Child
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
